import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface AwardShortlistBook {
  title: string;
  author: string;
  position: number;
  isbn13_primary?: string;
  isbn13_alternates?: string[];
  isbn10_primary?: string;
  isbn10_alternates?: string[];
  bookId?: string;
  coverUrl?: string;
  matchConfidence?: 'high' | 'medium' | 'low' | 'none';
  enrichmentStatus?: 'pending' | 'enriching' | 'matched' | 'failed';
  triedIsbns?: string[];
}

export const useAwardShortlistSuggestion = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const suggestShortlist = async (
    prizeName: string,
    year: number,
    onProgress?: (book: AwardShortlistBook, index: number, total: number) => void
  ): Promise<AwardShortlistBook[]> => {
    setLoading(true);
    setError(null);

    try {
      console.log(`🏆 Requesting shortlist for ${prizeName} ${year}`);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const { data, error: invokeError } = await supabase.functions.invoke('suggest-award-shortlist', {
        body: { prize_name: prizeName, year },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      if (invokeError) throw invokeError;
      
      // Handle case where AI doesn't have data
      if (data?.error === 'no_data' || !data?.shortlist || data.shortlist.length === 0) {
        throw new Error(
          data?.message || 
          `Couldn't find shortlist data for ${prizeName} ${year}. Please add books manually.`
        );
      }

      console.log(`📚 Received ${data.shortlist.length} books from AI`);

      // Set initial status
      const booksWithStatus = data.shortlist.map((book: any) => ({
        ...book,
        enrichmentStatus: 'pending' as const,
        triedIsbns: [],
      }));

      // Enrich books one by one with progress updates
      const enrichedBooks: AwardShortlistBook[] = [];
      for (let i = 0; i < booksWithStatus.length; i++) {
        const book = booksWithStatus[i];
        
        if (onProgress) {
          onProgress({ ...book, enrichmentStatus: 'enriching' }, i, booksWithStatus.length);
        }

        const enriched = await enrichSingleBook(book);
        enrichedBooks.push(enriched);

        if (onProgress) {
          onProgress(enriched, i + 1, booksWithStatus.length);
        }
      }

      console.log(`✨ Enriched ${enrichedBooks.length} books`);
      return enrichedBooks;
    } catch (err: any) {
      console.error('❌ Error suggesting shortlist:', err);
      setError(err.message || 'Failed to get shortlist suggestions');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { suggestShortlist, loading, error };
};

async function enrichSingleBook(book: AwardShortlistBook): Promise<AwardShortlistBook> {
  const triedIsbns: string[] = [];
  
  // Collect all ISBNs to try
  const isbnCandidates: string[] = [];
  if (book.isbn13_primary) isbnCandidates.push(book.isbn13_primary);
  if (book.isbn13_alternates) isbnCandidates.push(...book.isbn13_alternates);
  if (book.isbn10_primary) isbnCandidates.push(book.isbn10_primary);
  if (book.isbn10_alternates) isbnCandidates.push(...book.isbn10_alternates);

  console.log(`[enrichSingleBook] Trying ${isbnCandidates.length} ISBNs for "${book.title}"`);

  // Try each ISBN
  for (const isbn of isbnCandidates) {
    if (!isbn || isbn.trim().length === 0) continue;
    
    triedIsbns.push(isbn);
    
    try {
      // Search our database first
      const { data: existingBooks } = await supabase
        .from('books')
        .select('id, title, authors, cover_url, cover_cache_url, isbn13, isbn10')
        .or(`isbn13.eq.${isbn},isbn10.eq.${isbn}`)
        .limit(1);

      if (existingBooks && existingBooks.length > 0) {
        const match = existingBooks[0];
        console.log(`✅ Found in DB by ISBN ${isbn}:`, match.title);
        
        return {
          ...book,
          bookId: match.id,
          coverUrl: match.cover_cache_url || match.cover_url || undefined,
          matchConfidence: 'high',
          enrichmentStatus: 'matched',
          triedIsbns,
        };
      }
    } catch (error) {
      console.error(`Error searching by ISBN ${isbn}:`, error);
    }
  }

  // If no ISBN match, try title/author match
  console.log(`[enrichSingleBook] No ISBN match, trying title/author for "${book.title}"`);
  
  try {
    const { data: titleMatches } = await supabase
      .from('books')
      .select('id, title, authors, cover_url, cover_cache_url')
      .ilike('title', `%${book.title}%`)
      .limit(5);

    if (titleMatches && titleMatches.length > 0) {
      // Find best author match
      const authorLower = book.author.toLowerCase();
      const bestMatch = titleMatches.find(b => 
        b.authors?.some(a => a.toLowerCase().includes(authorLower) || authorLower.includes(a.toLowerCase()))
      );

      if (bestMatch) {
        console.log(`✅ Found by title/author:`, bestMatch.title);
        return {
          ...book,
          bookId: bestMatch.id,
          coverUrl: bestMatch.cover_cache_url || bestMatch.cover_url || undefined,
          matchConfidence: 'medium',
          enrichmentStatus: 'matched',
          triedIsbns,
        };
      }
    }
  } catch (error) {
    console.error(`Error in title/author search:`, error);
  }

  // No match found
  console.log(`❌ No match found for "${book.title}"`);
  return {
    ...book,
    matchConfidence: 'none',
    enrichmentStatus: 'failed',
    triedIsbns,
  };
}
